import { FC, useEffect, useState } from 'react';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRowModel } from '@mui/x-data-grid';
import { useGetStoresListNoCacheQuery, useGetSuburbNoCacheQuery, useUpdateSuburbMutation } from '@services';
import { AvaSuburbs, JimboResponse } from '@models';
import { Grid, Box, Typography, Button } from '@mui/material';
import { LoadingSpinner } from '@components';
import { showToast } from '@store/slices/notification';
import { useAppDispatch } from '@hooks/redux-custom';
import { LoadingButton } from '@mui/lab';
import { changeToNewRanking, findRankingChangedStores } from './AvaStoreRanking.service';
import { SuburbEditModal } from './SuburbEditModal';

type Props = {
  searchSuburb: string;
  setLoading: (isLoading: boolean) => void;
};

type StoreRanking = {
  store_code: number;
  rank: number;
};

export const AvaStoreRanking: FC<Props> = ({ searchSuburb, setLoading }) => {
  const {
    data: suburb,
    isLoading,
    refetch,
  } = useGetSuburbNoCacheQuery({
    suburb: searchSuburb,
  });
  const { data: stores } = useGetStoresListNoCacheQuery();
  const [localStoreRanking, setLocalStoreRanking] = useState<StoreRanking[]>([]);
  const [isOrderChanged, setIsOrderChanged] = useState<boolean>(false);
  const [updateStoreRankProcessing, setIsUpdateStoreRankProcessing] = useState<boolean>(false);
  const [row, setRow] = useState<StoreRanking[]>([]);
  const [isSuburbModalOpen, setSuburbModalOpen] = useState(false);
  const [gridKey, setGridKey] = useState<number>(0);
  const [updateSuburb] = useUpdateSuburbMutation();
  const dispatch = useAppDispatch();

  const columns: GridColDef[] = [
    {
      field: 'store_code',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Store Code'}
        </Typography>
      ),
      flex: 0.5,
    },
    {
      field: 'store_name',
      headerName: 'Store Name',
      flex: 1,
      valueGetter: (params) => {
        const store = stores?.find((store) => store.store_code === params.row.store_code);
        return store?.name || 'N/A';
      },
    },
    {
      field: 'rank',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Rank'}
        </Typography>
      ),
      flex: 1,
      editable: true,
    },
  ];

  const processRowUpdate = async (newRow: GridRowModel, oldRow: GridRowModel) => {
    return new Promise((resolve, reject) => {
      const newRanking = JSON.parse(JSON.stringify(localStoreRanking));

      if (isNaN(newRow.rank) || newRow.rank === oldRow.rank) {
        newRow = oldRow;
      } else {
        newRow.rank = parseInt(newRow.rank);
        const changedProducts = changeToNewRanking(
          newRanking as unknown as StoreRanking[],
          oldRow.store_code,
          newRow.rank,
        ) as unknown as StoreRanking;

        setIsOrderChanged(true);
        setLocalStoreRanking(changedProducts as unknown as StoreRanking[]);
      }

      resolve(newRow);
    });
  };

  const saveStoreRanking = async () => {
    const rankingChangedStores = findRankingChangedStores(suburb?.store_ranking as StoreRanking[], localStoreRanking as StoreRanking[]);

    if (rankingChangedStores) {
      setIsUpdateStoreRankProcessing(true);
      const listsToUpdate: Partial<AvaSuburbs>[] = [];

      listsToUpdate.push({
        key: suburb?.key,
        suburb_name: suburb?.suburb_name,
        timezone: suburb?.timezone,
        suburb_state: suburb?.suburb_state,
        suburb_postcode: suburb?.suburb_postcode ? suburb?.suburb_postcode?.toString() : '0000',
        store_ranking: localStoreRanking,
      });

      listsToUpdate &&
        updateSuburb(listsToUpdate)
          .unwrap()
          .then((response) => {
            refetch();
            setGridKey((prevKey) => prevKey + 1);
            setIsUpdateStoreRankProcessing(false);
            setIsOrderChanged(false);

            dispatch(
              showToast({
                message: response,
                type: 'success',
              }),
            );
          })
          .catch((error: { data: JimboResponse }) => {
            console.log(error);

            dispatch(
              showToast({
                message: 'Failed to update store rankings for suburb ' + suburb?.suburb_name,
                type: 'error',
              }),
            );

            setIsUpdateStoreRankProcessing(false);
          });
    } else {
      dispatch(
        showToast({
          message: 'Please change product rankings to before clicking the save button!',
          type: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    if (suburb && suburb.store_ranking) {
      setLoading(true);
      setRow(suburb.store_ranking);
      setLoading(false);
    }
  }, [suburb]);

  useEffect(() => {
    setRow(localStoreRanking);
  }, [localStoreRanking]);

  useEffect(() => {
    if (suburb?.store_ranking) {
      setLocalStoreRanking(suburb.store_ranking);
    }
  }, [suburb?.store_ranking]);

  const handleClose = () => {
    setSuburbModalOpen(false);
    refetch();
    setGridKey((prevKey) => prevKey + 1);
  };

  return isLoading ? (
    <LoadingSpinner isOpen={isLoading} />
  ) : (
    <Grid data-testid="store-ranking-sort-grid" container spacing={2} sx={{ py: 2 }}>
      <Grid item xs={10}>
        <Box sx={{ height: 800, width: '100%' }}>
          {!!row && (
            <DataGrid
              key={gridKey}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'rank', sort: 'asc' }],
                },
              }}
              rows={row}
              columns={columns}
              processRowUpdate={processRowUpdate}
              experimentalFeatures={{ newEditingApi: true }}
              sx={{
                '.MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#CD9BF0',
                  color: '#161616',
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: '#faf2ff',
                  },
                },
              }}
              getRowId={(row) => row.store_code}
            />
          )}
        </Box>
      </Grid>
      <Grid item xs={2}>
        <LoadingButton
          loading={updateStoreRankProcessing}
          variant="contained"
          disabled={!isOrderChanged}
          onClick={() => {
            saveStoreRanking();
          }}
        >
          Save Store Ranking
        </LoadingButton>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          fullWidth
          onClick={() => {
            setSuburbModalOpen(true);
          }}
        >
          Add New Store
        </Button>
        {suburb && isSuburbModalOpen && (
          <SuburbEditModal
            isOpen={isSuburbModalOpen}
            onClose={() => {
              handleClose();
            }}
            searchSuburb={suburb.key}
          />
        )}
      </Grid>
    </Grid>
  );
};
